import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PackageCheck, Download, Printer, Send } from "lucide-react";
import LogReportDialog from "@/components/LogReportDialog";

/**
 * ItemsReportDialog — read-only popup showing all items received in a single log entry.
 * Reuses the log record's own `items[]` + top-level meta (received_by, received_at).
 * Provides quick actions: Download CSV · Print · Email (opens LogReportDialog manifest mode).
 */
export default function ItemsReportDialog({ log, open, onClose }) {
  const [emailOpen, setEmailOpen] = useState(false);
  if (!log) return null;
  const items = Array.isArray(log.items) && log.items.length > 0
    ? log.items
    : [{ item_type: log.item_type || "Parcel", name: log.notes || "—", quantity: log.shipment_count || 1, parcel_condition: log.parcel_condition || "Good" }];
  const receivedBy = log.delivery_staff_name || log.employee_name || log.created_by_name || "—";
  const receivedAt = (log.created_at || "").slice(0, 19).replace("T", " ") + " UTC";
  // Infer direction from log shape (inward = has mode/courier, outward = has employee_name/drop_type)
  const direction = (log.mode || log.courier) ? "inward"
                  : (log.employee_name || log.drop_type) ? "outward" : "inward";

  const downloadCsv = () => {
    const rows = [["Item Name", "Item Type", "Quantity", "Parcel Condition", "Received By", "Date & Time Received"]];
    items.forEach((it) => rows.push([
      it.name || "—", it.item_type || "—", it.quantity ?? 1,
      it.parcel_condition || "—", receivedBy, receivedAt,
    ]));
    const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `items-report-${(log.id || "").slice(0, 8)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const doPrint = () => window.print();

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-md max-w-3xl p-0 flex flex-col max-h-[85vh] gap-0" data-testid="log-items-report-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-display text-2xl flex items-center gap-2"><PackageCheck size={18} className="text-primary"/> Items Report</DialogTitle>
              <div className="text-xs text-muted-foreground mono mt-1">Log · {log.id?.slice(0, 8)} · {receivedAt}</div>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" data-testid="items-report-download-csv" onClick={downloadCsv}
                      title="Download CSV"
                      className="inline-flex items-center gap-1.5 border border-border rounded-md px-2.5 py-1 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card">
                <Download size={12} /> CSV
              </button>
              <button type="button" data-testid="items-report-print" onClick={doPrint}
                      title="Print / PDF"
                      className="inline-flex items-center gap-1.5 border border-border rounded-md px-2.5 py-1 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card">
                <Printer size={12} /> Print
              </button>
              <button type="button" data-testid="items-report-email" onClick={() => setEmailOpen(true)}
                      title="Email manifest"
                      className="inline-flex items-center gap-1.5 border border-primary text-primary rounded-md px-2.5 py-1 text-xs font-semibold hover:bg-primary hover:text-white transition-colors">
                <Send size={12} /> Email
              </button>
            </div>
          </div>
        </DialogHeader>
        <div className="overflow-y-auto p-0">
          <table className="w-full text-xs" data-testid="log-items-report-table">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border sticky top-0 bg-muted/40">
              <tr>
                <th className="text-left p-3">Item Name</th>
                <th className="text-left p-3">Item Type</th>
                <th className="text-left p-3">Quantity</th>
                <th className="text-left p-3">Parcel Condition</th>
                <th className="text-left p-3">Received By</th>
                <th className="text-left p-3">Date &amp; Time Received</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-border" data-testid={`log-items-report-row-${i}`}>
                  <td className="p-3 font-semibold text-foreground">{it.name || "—"}</td>
                  <td className="p-3">{it.item_type || "—"}</td>
                  <td className="p-3 mono">{it.quantity ?? 1}</td>
                  <td className="p-3">
                    <span className="text-[10px] uppercase tracking-widest border border-border rounded px-2 py-0.5">{it.parcel_condition || "—"}</span>
                  </td>
                  <td className="p-3">{receivedBy}</td>
                  <td className="p-3 mono text-muted-foreground">{receivedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="shrink-0 px-6 py-3 border-t border-border bg-white flex justify-end">
          <button type="button" onClick={onClose} data-testid="log-items-report-close"
            className="text-xs font-semibold text-primary hover:underline">Close</button>
        </div>
      </DialogContent>

      {/* Email/Download full manifest via existing backend endpoints (PDF/Excel/CSV + To/CC/BCC/Subject/Message) */}
      <LogReportDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        direction={direction}
        logId={log.id}
        manifestMode={true}
      />
    </Dialog>
  );
}
